import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../api';
import { useBranding } from '../../context/BrandingContext';
import { Palette, Building2, Image, Save, RotateCcw } from 'lucide-react';

const BrandingPage = () => {
  const { branding, refreshBranding } = useBranding();
  const [formData, setFormData] = useState({
    company_name: '',
    company_logo_url: '',
    company_logo_small_url: '',
    primary_color: '#14b8a6',
    secondary_color: '#0f172a',
    login_title: '',
    footer_text: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    setLoading(true);
    try {
      const response = await api.get('/branding');
      setFormData({
        company_name: response.data.company_name || '',
        company_logo_url: response.data.company_logo_url || '',
        company_logo_small_url: response.data.company_logo_small_url || '',
        primary_color: response.data.primary_color || '#14b8a6',
        secondary_color: response.data.secondary_color || '#0f172a',
        login_title: response.data.login_title || '',
        footer_text: response.data.footer_text || ''
      });
    } catch (error) {
      console.error('Error fetching branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/branding', formData);
      toast.success('Configuración guardada exitosamente');
      refreshBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({
      company_name: branding.company_name || '',
      company_logo_url: branding.company_logo_url || '',
      company_logo_small_url: branding.company_logo_small_url || '',
      primary_color: branding.primary_color || '#14b8a6',
      secondary_color: branding.secondary_color || '#0f172a',
      login_title: branding.login_title || '',
      footer_text: branding.footer_text || ''
    });
    toast.info('Cambios descartados');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Palette className="h-8 w-8 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-semibold">Personalización</h1>
            <p className="text-sm text-muted-foreground">Configura la marca y apariencia del sistema</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información de la Empresa */}
          <Card className="card-subtle">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                <Building2 className="h-4 w-4" strokeWidth={1.5} />
                Información de la Empresa
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Nombre de la Empresa</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="Mi Empresa S.A."
                  />
                  <p className="text-xs text-muted-foreground">Se muestra en el login y navbar</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login_title">Subtítulo del Login</Label>
                  <Input
                    id="login_title"
                    value={formData.login_title}
                    onChange={(e) => handleChange('login_title', e.target.value)}
                    placeholder="Panel de Administración"
                  />
                  <p className="text-xs text-muted-foreground">Texto debajo del nombre en login</p>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="footer_text">Texto del Footer</Label>
                  <Input
                    id="footer_text"
                    value={formData.footer_text}
                    onChange={(e) => handleChange('footer_text', e.target.value)}
                    placeholder="Powered by i4-Assets"
                  />
                  <p className="text-xs text-muted-foreground">Se muestra en el pie de página</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logos */}
          <Card className="card-subtle">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                <Image className="h-4 w-4" strokeWidth={1.5} />
                Logos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_logo_url">Logo Principal (Login)</Label>
                  <Input
                    id="company_logo_url"
                    value={formData.company_logo_url}
                    onChange={(e) => handleChange('company_logo_url', e.target.value)}
                    placeholder="https://ejemplo.com/logo.png"
                  />
                  <p className="text-xs text-muted-foreground">Tamaño recomendado: 200x60 px</p>
                  {formData.company_logo_url && (
                    <div className="mt-2 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                      <img 
                        src={formData.company_logo_url} 
                        alt="Preview logo" 
                        className="h-12 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_logo_small_url">Logo Pequeño (Navbar)</Label>
                  <Input
                    id="company_logo_small_url"
                    value={formData.company_logo_small_url}
                    onChange={(e) => handleChange('company_logo_small_url', e.target.value)}
                    placeholder="https://ejemplo.com/logo-small.png"
                  />
                  <p className="text-xs text-muted-foreground">Tamaño recomendado: 32x32 px</p>
                  {formData.company_logo_small_url && (
                    <div className="mt-2 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                      <img 
                        src={formData.company_logo_small_url} 
                        alt="Preview logo small" 
                        className="h-8 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="card-subtle">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Vista Previa
              </h3>
              <div className="bg-slate-800 rounded-lg p-4 space-y-4">
                {/* Simulated Navbar */}
                <div className="flex items-center gap-3 pb-3 border-b border-slate-700">
                  {formData.company_logo_small_url ? (
                    <img src={formData.company_logo_small_url} alt="Logo" className="h-7" onError={(e) => e.target.style.display = 'none'} />
                  ) : (
                    <div className="h-7 w-7 bg-primary/20 rounded flex items-center justify-center text-primary text-xs font-bold">
                      {formData.company_name?.charAt(0) || 'E'}
                    </div>
                  )}
                  <div>
                    <span className="text-white font-semibold text-sm">{formData.company_name || 'Mi Empresa'}</span>
                    <span className="text-slate-500 text-xs block">{formData.login_title || 'Panel de Administración'}</span>
                  </div>
                </div>
                
                {/* Simulated Footer */}
                <div className="text-xs text-slate-400 flex justify-between pt-2 border-t border-slate-700">
                  <span>© 2026 {formData.company_name || 'Mi Empresa'}</span>
                  <span>{formData.footer_text || 'Powered by i4-Assets'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Descartar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Guardando...
                </span>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Guardar Cambios
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default BrandingPage;
